import axios from 'axios';
import config from '../config';

const TOKEN_KEY = 'fpr_token';
const LEGACY_TOKEN_KEY = 'fpr_token';

/**
 * Return the active wizard JWT for this tab.
 * Prefers sessionStorage and migrates any legacy localStorage token once.
 */
function getStoredToken() {
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  if (sessionToken) {
    return sessionToken;
  }

  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacyToken) {
    sessionStorage.setItem(TOKEN_KEY, legacyToken);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    return legacyToken;
  }

  return null;
}

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 15000,
});

api.interceptors.request.use((req) => {
  const token = getStoredToken();
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// ---------------------------------------------------------------------------
// Client auth
// ---------------------------------------------------------------------------

export async function loginWithToken(token) {
  const res = await api.post('/auth/fpr/login', { token });
  return res.data;
}

export async function devLogin(clientType = 'hff') {
  const res = await api.post(`/auth/fpr/dev-login?client_type=${clientType}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Wizard progress (client-facing)
// ---------------------------------------------------------------------------

export async function getProgress() {
  const res = await api.get('/wizard/progress');
  return res.data;
}

function getDeviceType() {
  const ua = navigator.userAgent || '';
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'mobile' : 'desktop';
}

/**
 * Persist wizard progress for the authenticated client.
 *
 * @param {number} currentStep
 * @param {object} formData
 * @param {boolean} [completed=false]
 * @param {string|null} [clientId=null] Authenticated client id from React state.
 */
export async function saveProgress(
  currentStep,
  formData,
  completed = false,
  clientId = null,
) {
  const res = await api.put('/wizard/progress', {
    current_step: currentStep,
    form_data: formData,
    completed,
    device_type: getDeviceType(),
    // Server rejects saves when this does not match the JWT subject.
    client_id: clientId || undefined,
  });
  return res.data;
}

export async function getMe() {
  const res = await api.get('/wizard/me');
  return res.data;
}

// ---------------------------------------------------------------------------
// Admin (advisor-facing)
// ---------------------------------------------------------------------------

export async function createClient(clientData, advisorToken) {
  const res = await axios.post(`${config.apiUrl}/admin/fpr/clients`, clientData, {
    headers: { Authorization: `Bearer ${advisorToken}` },
  });
  return res.data;
}

export async function listClients(advisorToken) {
  const res = await axios.get(`${config.apiUrl}/admin/fpr/clients`, {
    headers: { Authorization: `Bearer ${advisorToken}` },
  });
  return res.data;
}

export default api;
